/**
 * Things in the air: striped hot-air balloons drifting on the breeze, and
 * birds — gulls gliding, parrots flapping, sunbirds darting between flowers.
 * Everything here moves, so these layers only have a `moving` part.
 */
import type { BalloonsLayer, BirdsLayer } from '@dropstack/core';
import { lerp, wrap, type SceneSize } from '../layout.ts';
import { range, rngFor } from '../noise.ts';
import { circlePath, ellipsePath, gloss, leafPath, pick, rgba, rimOf, roundRectPath, shade, strokeRim, vGradient, type Ctx } from './draw.ts';
import type { PaintEnv, Painter } from './types.ts';

interface Flyer {
  /** Start position along the drift track, 0–1. */
  track: number;
  y: number;
  /** Size in px (envelope width or wingspan). */
  s: number;
  phase: number;
  i: number;
}

function planFlyers(count: number, yMin: number, yMax: number, sizeUnits: number, env: PaintEnv): Flyer[] {
  const { size, seed } = env;
  const rng = rngFor(seed, 1);
  const n = Math.max(0, Math.floor(count));
  return Array.from({ length: n }, (_, i) => ({
    track: (i + range(rng, 0.1, 0.9)) / Math.max(1, n),
    y: lerp(yMin, yMax, n === 1 ? 0.5 : (i + rng.next()) / n) * size.height,
    s: sizeUnits * size.unit * range(rng, 0.8, 1.2),
    phase: range(rng, 0, Math.PI * 2),
    i,
  }));
}

/** x of a flyer drifting right at `speed` units/min, wrapping around a track wider than the view. */
function driftX(f: Flyer, speed: number, size: SceneSize, time: number, margin: number): number {
  const min = -size.bleed - margin;
  const max = size.width + size.bleed + margin;
  return wrap(min + f.track * (max - min) + (speed * size.unit * time) / 60, min, max);
}

// ───────────── balloons ─────────────

function balloon(ctx: Ctx, x: number, y: number, w: number, c1: string, c2: string, size: SceneSize): void {
  const cy = y - w * 0.1;
  const envelope = () => {
    ctx.beginPath();
    ctx.moveTo(x - w * 0.13, y + w * 0.55);
    ctx.bezierCurveTo(x - w * 0.34, y + w * 0.36, x - w * 0.52, y + w * 0.14, x - w * 0.5, cy);
    ctx.arc(x, cy, w * 0.5, Math.PI, 0);
    ctx.bezierCurveTo(x + w * 0.52, y + w * 0.14, x + w * 0.34, y + w * 0.36, x + w * 0.13, y + w * 0.55);
    ctx.closePath();
  };
  // Ropes and basket.
  const by = y + w * 0.72;
  ctx.beginPath();
  ctx.moveTo(x - w * 0.12, y + w * 0.56);
  ctx.lineTo(x - w * 0.1, by);
  ctx.moveTo(x + w * 0.12, y + w * 0.56);
  ctx.lineTo(x + w * 0.1, by);
  ctx.strokeStyle = '#8b5a3c';
  ctx.lineWidth = Math.max(1, w * 0.02);
  ctx.stroke();
  ctx.beginPath();
  roundRectPath(ctx, x - w * 0.12, by, w * 0.24, w * 0.15, w * 0.04);
  ctx.fillStyle = vGradient(ctx, by, by + w * 0.15, ['#d39a62', '#b07a4a']);
  ctx.fill();
  strokeRim(ctx, '#7a4f2e', size, 0.8);
  ctx.beginPath();
  ctx.moveTo(x - w * 0.12, by + w * 0.06);
  ctx.lineTo(x + w * 0.12, by + w * 0.06);
  ctx.strokeStyle = 'rgba(122, 79, 46, 0.6)';
  ctx.lineWidth = Math.max(1, w * 0.015);
  ctx.stroke();
  // Envelope with curved gores in the second colour.
  envelope();
  ctx.fillStyle = c1;
  ctx.fill();
  ctx.save();
  ctx.clip();
  ctx.beginPath();
  for (const u of [-0.56, 0, 0.56]) ellipsePath(ctx, x + u * w, cy + w * 0.1, w * 0.13, w * 0.8);
  ctx.fillStyle = c2;
  ctx.fill();
  const light = ctx.createRadialGradient(x - w * 0.2, cy - w * 0.22, 0, x, cy + w * 0.1, w * 0.75);
  light.addColorStop(0, 'rgba(255, 255, 255, 0.4)');
  light.addColorStop(0.5, 'rgba(255, 255, 255, 0)');
  light.addColorStop(1, 'rgba(40, 20, 60, 0.18)');
  ctx.fillStyle = light;
  ctx.fillRect(x - w, cy - w, w * 2, w * 2);
  ctx.restore();
  envelope();
  strokeRim(ctx, rimOf(c1), size);
  ctx.beginPath();
  roundRectPath(ctx, x - w * 0.14, y + w * 0.52, w * 0.28, w * 0.07, w * 0.03);
  ctx.fillStyle = shade(c2, -0.15);
  ctx.fill();
  strokeRim(ctx, rimOf(c2), size, 0.6);
  gloss(ctx, x - w * 0.22, cy - w * 0.25, w * 0.1, w * 0.06, 0.55, -0.7);
}

export const balloonsPainter: Painter<BalloonsLayer> = {
  extent: (layer, { size }) => ({
    top: layer.yMin * size.height - layer.size * size.unit * 1.5,
    bottom: layer.yMax * size.height + layer.size * size.unit * 1.5,
  }),
  moving(ctx, layer, env) {
    const { size, time } = env;
    const colors = layer.colors.length ? layer.colors : ['#ff6b6b', '#ffd43b'];
    for (const f of planFlyers(layer.count, layer.yMin, layer.yMax, layer.size, env)) {
      const x = driftX(f, layer.speed, size, time, f.s);
      const y = f.y + Math.sin(time * 0.5 + f.phase) * size.unit * 0.01;
      const c1 = pick(colors, f.i * 2);
      const c2 = colors.length > 1 ? pick(colors, f.i * 2 + 1) : '#ffffff';
      balloon(ctx, x, y, f.s, c1, c2, size);
    }
  },
};

// ───────────── birds ─────────────

/** A gliding gull: the classic soft "m", with a darker rim so white gulls read on a pale sky. */
function gull(ctx: Ctx, x: number, y: number, s: number, color: string, flap: number, size: SceneSize): void {
  const tip = s * 0.16 * Math.sin(flap);
  const path = () => {
    ctx.beginPath();
    ctx.moveTo(x - s / 2, y - tip);
    ctx.quadraticCurveTo(x - s * 0.22, y - s * 0.2 - tip * 0.3, x, y);
    ctx.quadraticCurveTo(x + s * 0.22, y - s * 0.2 - tip * 0.3, x + s / 2, y - tip);
  };
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  path();
  ctx.strokeStyle = rgba(shade(color, color.toLowerCase() === '#ffffff' ? -0.35 : -0.3), 0.85);
  ctx.lineWidth = s * 0.1 + size.unit * 0.004;
  ctx.stroke();
  path();
  ctx.strokeStyle = color;
  ctx.lineWidth = s * 0.1;
  ctx.stroke();
}

/** A cartoon parrot in profile, facing right (the caller mirrors it). */
function parrot(ctx: Ctx, s: number, body: string, wing: string, flap: number, size: SceneSize): void {
  // Tail feathers.
  ctx.beginPath();
  leafPath(ctx, -s * 0.42, s * 0.1, s * 0.46, s * 0.07, Math.PI + 0.25);
  leafPath(ctx, -s * 0.38, s * 0.06, s * 0.38, s * 0.06, Math.PI + 0.05);
  ctx.fillStyle = wing;
  ctx.fill();
  strokeRim(ctx, rimOf(wing), size, 0.6);
  // Far wing.
  const wingAngle = -Math.PI / 2 - 0.5 + Math.sin(flap) * 0.9;
  ctx.beginPath();
  leafPath(ctx, -s * 0.02 + Math.cos(wingAngle - 0.3) * s * 0.2, Math.sin(wingAngle - 0.3) * s * 0.2, s * 0.42, s * 0.09, wingAngle - 0.3);
  ctx.fillStyle = shade(wing, -0.15);
  ctx.fill();
  // Body and head.
  ctx.beginPath();
  ellipsePath(ctx, 0, 0, s * 0.24, s * 0.13, -0.25);
  circlePath(ctx, s * 0.2, -s * 0.09, s * 0.1);
  ctx.fillStyle = vGradient(ctx, -s * 0.2, s * 0.13, [shade(body, 0.15), body]);
  ctx.fill();
  strokeRim(ctx, rimOf(body), size, 0.7);
  // Beak and eye.
  ctx.beginPath();
  ctx.moveTo(s * 0.28, -s * 0.13);
  ctx.quadraticCurveTo(s * 0.4, -s * 0.12, s * 0.36, -s * 0.02);
  ctx.quadraticCurveTo(s * 0.32, -s * 0.07, s * 0.28, -s * 0.05);
  ctx.closePath();
  ctx.fillStyle = '#fff3bf';
  ctx.fill();
  strokeRim(ctx, '#b08a3c', size, 0.5);
  ctx.beginPath();
  circlePath(ctx, s * 0.22, -s * 0.11, s * 0.035);
  ctx.fillStyle = '#ffffff';
  ctx.fill();
  ctx.beginPath();
  circlePath(ctx, s * 0.23, -s * 0.11, s * 0.018);
  ctx.fillStyle = '#1d2a44';
  ctx.fill();
  // Near wing.
  ctx.beginPath();
  leafPath(ctx, Math.cos(wingAngle) * s * 0.2, Math.sin(wingAngle) * s * 0.2 - s * 0.02, s * 0.44, s * 0.1, wingAngle);
  ctx.fillStyle = wing;
  ctx.fill();
  strokeRim(ctx, rimOf(wing), size, 0.6);
}

/** A tiny sunbird with a long curved beak and a bright chest, wings a quick blur. */
function sunbird(ctx: Ctx, s: number, body: string, chest: string, flap: number, size: SceneSize): void {
  ctx.beginPath();
  leafPath(ctx, -s * 0.36, s * 0.06, s * 0.34, s * 0.035, Math.PI + 0.15);
  ctx.fillStyle = shade(body, -0.15);
  ctx.fill();
  ctx.beginPath();
  ellipsePath(ctx, 0, 0, s * 0.22, s * 0.12, -0.2);
  circlePath(ctx, s * 0.17, -s * 0.08, s * 0.09);
  ctx.fillStyle = body;
  ctx.fill();
  strokeRim(ctx, rimOf(body), size, 0.6);
  ctx.beginPath();
  ellipsePath(ctx, s * 0.08, s * 0.04, s * 0.11, s * 0.07, -0.3);
  ctx.fillStyle = chest;
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(s * 0.25, -s * 0.08);
  ctx.quadraticCurveTo(s * 0.42, -s * 0.08, s * 0.5, s * 0.04);
  ctx.strokeStyle = '#2b2b3a';
  ctx.lineWidth = Math.max(1, s * 0.03);
  ctx.lineCap = 'round';
  ctx.stroke();
  ctx.beginPath();
  circlePath(ctx, s * 0.2, -s * 0.1, s * 0.022);
  ctx.fillStyle = '#1d2a44';
  ctx.fill();
  // Blurred wings.
  const spread = 0.6 + 0.4 * Math.sin(flap);
  ctx.beginPath();
  ellipsePath(ctx, -s * 0.02, -s * 0.18 * spread, s * 0.08, s * 0.2 * spread, -0.35);
  ctx.fillStyle = rgba(shade(body, 0.3), 0.65);
  ctx.fill();
}

export const birdsPainter: Painter<BirdsLayer> = {
  extent: (layer, { size }) => ({
    top: layer.yMin * size.height - layer.size * size.unit - size.unit * 0.1,
    bottom: layer.yMax * size.height + layer.size * size.unit + size.unit * 0.1,
  }),
  moving(ctx, layer, env) {
    const { size, time } = env;
    const colors = layer.colors.length ? layer.colors : ['#ffffff'];
    for (const f of planFlyers(layer.count, layer.yMin, layer.yMax, layer.size, env)) {
      const color = pick(colors, f.i);
      if (layer.style === 'sunbird') {
        // Sunbirds hover and dart around a slowly drifting home point.
        const home = driftX(f, layer.speed * 0.3, size, time, f.s * 2);
        const x = home + Math.sin(time * 0.5 + f.phase) * size.unit * 0.08 + Math.sin(time * 1.7 + f.phase * 2) * size.unit * 0.02;
        const vx = Math.cos(time * 0.5 + f.phase) * 0.5 + Math.cos(time * 1.7 + f.phase * 2) * 0.34;
        const y = f.y + Math.sin(time * 0.9 + f.phase) * size.unit * 0.03;
        ctx.save();
        ctx.translate(x, y);
        ctx.scale(vx >= 0 ? 1 : -1, 1);
        sunbird(ctx, f.s * 1.6, color, pick(colors, f.i + 1), time * 30 + f.phase, size);
        ctx.restore();
        continue;
      }
      const x = driftX(f, layer.speed, size, time, f.s);
      const y = f.y + Math.sin(time * 0.6 + f.phase) * f.s * 0.3;
      if (layer.style === 'gull') {
        gull(ctx, x, y, f.s, color, time * 2.2 + f.phase, size);
      } else {
        ctx.save();
        ctx.translate(x, y);
        parrot(ctx, f.s * 1.4, color, pick(colors, f.i + 2), time * 3.4 + f.phase, size);
        ctx.restore();
      }
    }
  },
};
